<div class="bg-surface rounded-lg overflow-hidden select-none">
  <!-- Week title -->
  <div
    class="px-4 py-2 text-sm text-fg-secondary font-medium border-b border-edge flex items-center justify-between gap-3"
  >
    <span>{{ weekTitle }}</span>
    <span class="text-xs text-fg-subtle whitespace-nowrap">
      Woche: {{ formatWorkHours(weekTotalHours) }}
    </span>
  </div>

  @let status = activeStatusMessage;
  <div [class]="statusLaneClasses(status)" role="status" aria-live="polite" data-week-status-lane>
    @if (status) {
      {{ status.text }}
    } @else {
      <span aria-hidden="true">&nbsp;</span>
    }
  </div>

  <!-- Day headers -->
  <div
    class="grid border-b border-edge overflow-y-hidden"
    style="grid-template-columns: 52px repeat(7, minmax(0, 1fr)); scrollbar-gutter: stable"
  >
    <div></div>
    @for (col of dayColumns; track col.date) {
      <button
        type="button"
        class="py-2 text-center text-sm cursor-pointer border-l border-edge relative"
        [ngClass]="densitySurfaceClasses(col)"
        [class.ring-1]="col.hasWarning"
        [class.ring-inset]="col.hasWarning"
        [class.ring-warning-ring]="col.hasWarning"
        (click)="selectedDateChange.emit(col.date)"
      >
        <span
          class="font-medium"
          [class.text-primary]="col.isToday"
          [class.text-fg-subtle]="!col.isToday"
          [class.text-fg-primary]="col.isSelected"
          >{{ col.dayLabel }}</span
        >
      </button>
    }
  </div>

  <!-- All-day section -->
  <div
    class="grid border-b border-edge bg-surface-secondary overflow-y-hidden"
    style="grid-template-columns: 52px repeat(7, minmax(0, 1fr)); scrollbar-gutter: stable"
  >
    <div class="text-[10px] text-fg-faint p-1 pt-2 text-right pr-2">Ganztag</div>
    @for (col of dayColumns; track col.date) {
      <div
        class="p-1 border-l border-edge min-w-0 min-h-[32px] space-y-0.5"
        [ngClass]="densitySurfaceClasses(col)"
        (click)="selectedDateChange.emit(col.date)"
      >
        @for (entry of col.allDayEntries; track entry.id) {
          <button
            type="button"
            data-week-entry
            class="cursor-pointer block w-full min-w-0 text-left"
            (click)="
              selectedDateChange.emit(col.date);
              editWork.emit({ work: entry });
              $event.stopPropagation()
            "
          >
            <app-week-work-card [work]="entry" />
          </button>
        }
        @for (timer of col.allDayTimers; track timer.id) {
          <button
            type="button"
            data-week-entry
            class="cursor-pointer block w-full min-w-0"
            (click)="
              selectedDateChange.emit(col.date);
              showTimer.emit({ timerId: timer.id, date: col.date });
              $event.stopPropagation()
            "
          >
            <app-week-timer-card
              [state]="timer.data.timerState"
              [totalMinutes]="timerTotalMinutes(timer)"
            />
          </button>
        }
      </div>
    }
  </div>

  <!-- Time grid -->
  <div class="overflow-y-auto" style="max-height: 560px; scrollbar-gutter: stable">
    <div
      #timeGrid
      class="grid"
      style="grid-template-columns: 52px repeat(7, minmax(0, 1fr))"
      (lostpointercapture)="onLostPointerCapture($event)"
    >
      <!-- Time labels column -->
      <div class="relative" [style.height.px]="gridTotalHours * pxPerHour">
        @for (slot of timeSlots; track slot; let i = $index) {
          @if (i > 0) {
            <div
              class="absolute right-0 pr-2 text-[10px] text-fg-faint text-right leading-none -translate-y-1/2"
              [style.top.px]="i * pxPerHour"
            >
              {{ slot }}
            </div>
          }
        }
      </div>

      <!-- Day columns -->
      @for (col of dayColumns; track col.date) {
        <div
          class="relative border-l border-edge cursor-crosshair focus:outline-none focus:ring-2 focus:ring-primary"
          role="group"
          tabindex="0"
          [attr.data-week-column-date]="col.date"
          [attr.aria-label]="
            'Zeitspalte ' + col.dayLabel + ': Enter erstellt eine neue Arbeitszeit'
          "
          [ngClass]="densitySurfaceClasses(col)"
          [class.ring-1]="col.hasWarning"
          [class.ring-inset]="col.hasWarning"
          [class.ring-warning-ring]="col.hasWarning"
          [style.height.px]="gridTotalHours * pxPerHour"
          (pointerdown)="onPointerDown($event, col)"
          (keydown)="onGridColumnKeydown($event, col)"
        >
          <!-- Horizontal hour lines -->
          @for (slot of timeSlots; track slot) {
            <div
              class="absolute left-0 right-0 border-b border-edge"
              [style.top.px]="$index * pxPerHour"
              [style.height.px]="pxPerHour"
            ></div>
          }

          <!-- Timed work entries -->
          @for (entry of col.timedEntries; track entry.id) {
            <div
              data-week-entry
              class="absolute left-0.5 right-0.5 z-10 group"
              [class.opacity-0]="isPendingWorkEntry(entry)"
              [style.top.px]="entry.topPx"
              [style.height.px]="entry.heightPx"
              (mousedown)="$event.stopPropagation()"
            >
              <button
                type="button"
                class="h-full w-full text-left focus:outline-none focus:ring-2 focus:ring-primary"
                [class.cursor-grab]="canMoveWorkEntry(entry)"
                [class.cursor-pointer]="!canMoveWorkEntry(entry)"
                (pointerdown)="onWorkEntryPointerDown($event, col, entry)"
                (click)="onWorkEntryClick($event, col, entry)"
              >
                <app-week-work-card [work]="entry.work" [sessionMinutes]="entry.sessionMinutes" />
              </button>
              @if (canResizeWorkEntry(entry, 'resize-start')) {
                <div
                  aria-hidden="true"
                  class="absolute inset-x-0 top-0 z-20 h-2 -translate-y-1/2 cursor-ns-resize bg-primary-soft-strong opacity-80 hover:opacity-100 focus:opacity-100"
                  (pointerdown)="onWorkEntryResizePointerDown($event, col, entry, 'resize-start')"
                ></div>
              }
              @if (canResizeWorkEntry(entry, 'resize-end')) {
                <div
                  aria-hidden="true"
                  class="absolute inset-x-0 bottom-0 z-20 h-2 translate-y-1/2 cursor-ns-resize bg-primary-soft-strong opacity-80 hover:opacity-100 focus:opacity-100"
                  (pointerdown)="onWorkEntryResizePointerDown($event, col, entry, 'resize-end')"
                ></div>
              }
            </div>
          }

          <!-- Timed timer entries -->
          @for (timerEntry of col.timedTimerEntries; track timerEntry.id) {
            <div
              data-week-entry
              class="absolute left-0.5 right-0.5 z-20 group"
              [class.opacity-0]="isPendingTimerEntry(timerEntry)"
              [style.top.px]="timerEntry.topPx"
              [style.height.px]="timerEntry.heightPx"
              (mousedown)="$event.stopPropagation()"
            >
              <button
                type="button"
                class="h-full w-full text-left focus:outline-none focus:ring-2 focus:ring-primary"
                [class.cursor-grab]="canMoveTimerEntry(timerEntry)"
                [class.cursor-pointer]="!canMoveTimerEntry(timerEntry)"
                (pointerdown)="onTimerEntryPointerDown($event, col, timerEntry)"
                (click)="onTimerEntryClick($event, col, timerEntry)"
              >
                <app-week-timer-card
                  [state]="timerEntry.timer.data.timerState"
                  [sessionMinutes]="timerEntry.sessionMinutes"
                  [totalMinutes]="timerEntry.totalMinutes"
                  [projectDisplayName]="timerEntry.projectDisplayName"
                  [positionDisplayName]="timerEntry.positionDisplayName"
                />
              </button>
              @if (canResizeTimerEntry(timerEntry, col, 'resize-start')) {
                <div
                  aria-hidden="true"
                  class="absolute inset-x-0 top-0 z-30 h-2 -translate-y-1/2 cursor-ns-resize bg-primary-soft-strong opacity-80 hover:opacity-100 focus:opacity-100"
                  (pointerdown)="
                    onTimerEntryResizePointerDown($event, col, timerEntry, 'resize-start')
                  "
                ></div>
              }
              @if (canResizeTimerEntry(timerEntry, col, 'resize-end')) {
                <div
                  aria-hidden="true"
                  class="absolute inset-x-0 bottom-0 z-30 h-2 translate-y-1/2 cursor-ns-resize bg-primary-soft-strong opacity-80 hover:opacity-100 focus:opacity-100"
                  (pointerdown)="
                    onTimerEntryResizePointerDown($event, col, timerEntry, 'resize-end')
                  "
                ></div>
              }
            </div>
          }

          @if (dragOverlayForColumn(col); as overlay) {
            <div
              class="absolute left-0 right-0 z-30 border border-primary rounded bg-primary-soft-strong pointer-events-none text-[10px] text-fg-primary px-1 flex items-center"
              [style.top.px]="overlay.topPx"
              [style.height.px]="overlay.heightPx"
            >
              {{ overlay.text }}
            </div>
          }
          @if (pendingOverlayForColumn(col); as overlay) {
            <div
              class="absolute left-0 right-0 z-30 border border-primary rounded bg-primary-soft-strong pointer-events-none text-[10px] text-fg-primary px-1 flex items-center"
              [style.top.px]="overlay.topPx"
              [style.height.px]="overlay.heightPx"
            >
              {{ overlay.text }}
            </div>
          }
        </div>
      }
    </div>
  </div>

  <!-- Footer: daily totals -->
  <div
    class="grid border-t border-edge bg-surface-secondary text-xs text-fg-secondary overflow-y-hidden"
    style="grid-template-columns: 52px repeat(7, minmax(0, 1fr)); scrollbar-gutter: stable"
  >
    <div class="p-2 text-right font-medium text-fg-faint">Σ</div>
    @for (col of dayColumns; track col.date) {
      <div
        class="p-2 text-center border-l border-edge"
        [ngClass]="densitySurfaceClasses(col)"
        [class.font-semibold]="col.totalHours > 0"
        [class.text-fg-subtle]="col.totalHours > 0"
      >
        @if (col.totalHours > 0) {
          {{ formatWorkHours(col.totalHours) }}
        }
      </div>
    }
  </div>
</div>
